import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../foundation/altzero_base/base/components/ui/select';
import { Checkbox } from '../../../../foundation/altzero_base/base/components/ui/checkbox';
import { emailApiService, EmailMessage } from '../../services/email/emailApiService';
import { useToast } from '../../../../foundation/altzero_base/base/hooks/use-toast';
import { Loader2, FileText, Download, Sparkles, Filter, Search, Paperclip } from 'lucide-react';
import { getTenders, Tender } from '../../services/tenderService';
import { PaginationBar } from './PaginationBar';

const PAGE_SIZE = 20;

interface RetailerResponseViewerProps {
    tenderId?: string;
    refreshTrigger?: number;
}

export const RetailerResponseViewer: React.FC<RetailerResponseViewerProps> = ({ tenderId: initialTenderId, refreshTrigger }) => {
    const { toast } = useToast();
    const [messages, setMessages] = useState<EmailMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const [tenders, setTenders] = useState<Tender[]>([]);

    // Filters
    const [selectedTenderId, setSelectedTenderId] = useState<string>(initialTenderId || 'all');
    const [selectedRetailer, setSelectedRetailer] = useState<string>('all');
    const [onlyWithAttachments, setOnlyWithAttachments] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    // Parsing state
    const [parsingAttachmentId, setParsingAttachmentId] = useState<string | null>(null);

    useEffect(() => {
        loadTenders();
    }, []);

    useEffect(() => {
        if (initialTenderId) {
            setSelectedTenderId(initialTenderId);
        }
    }, [initialTenderId]);

    useEffect(() => {
        fetchMessages();
    }, [selectedTenderId, onlyWithAttachments, refreshTrigger]);

    const loadTenders = async () => {
        try {
            const result = await getTenders();
            if (result.success && result.data) {
                setTenders(result.data);
            }
        } catch (error) {
            console.error('Failed to load tenders:', error);
        }
    };

    const fetchMessages = async () => {
        setLoading(true);
        try {
            // We need to implement getInboundMessages in emailApiService
            // For now, we'll assume it exists or we'll add it shortly.
            // Passing filters to the API
            const filters = {
                tenderId: selectedTenderId === 'all' ? undefined : selectedTenderId,
                hasAttachments: onlyWithAttachments,
                direction: 'inbound' as const
            };

            // @ts-ignore - We will add this method next
            const data = await emailApiService.getInboundMessages(filters);
            setMessages(data);
        } catch (error: any) {
            console.error('Error fetching messages:', error);
            toast({
                title: 'Error',
                description: error.message || 'Could not parse offer from attachment',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
            setParsingAttachmentId(null);
        }
    };

    const handleParseOffer = async (attachmentId: string, accountId: string) => {
        setParsingAttachmentId(attachmentId);
        try {
            const result = await emailApiService.parseAttachment(attachmentId, accountId);

            if (result.success) {
                toast({
                    title: 'Success',
                    description: 'Offer parsed successfully',
                    // @ts-ignore
                    variant: 'default',
                });
                // Refresh messages to show updated status
                fetchMessages();
            } else {
                toast({
                    title: 'Error',
                    description: 'Failed to parse offer',
                    variant: 'destructive',
                });
            }
        } catch (error: any) {
            console.error('Parse error:', error);
            toast({
                title: 'Error',
                description: error.message || 'Failed to parse offer',
                variant: 'destructive',
            });
        } finally {
            setParsingAttachmentId(null);
        }
    };

    const handleDownload = async (attachmentId: string, filename: string, accountId: string) => { // Need accountId
        try {
            const blob = await emailApiService.downloadAttachment(attachmentId, accountId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Download failed:', error);
            toast({
                title: 'Error',
                description: 'Failed to download attachment',
                variant: 'destructive',
            });
        }
    };

    // Filter messages client-side for retailer name and search query if API doesn't support it yet
    const filteredMessages = useMemo(() => messages.filter(msg => {
        const matchesRetailer = selectedRetailer === 'all' ||
            (msg as any).thread?.retailer?.name === selectedRetailer;

        const matchesSearch = searchQuery === '' ||
            msg.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
            msg.sender_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (msg as any).thread?.retailer?.name?.toLowerCase().includes(searchQuery.toLowerCase());

        return matchesRetailer && matchesSearch;
    }), [messages, selectedRetailer, searchQuery]);

    // Reset page on filter change
    useEffect(() => { setPage(1); }, [selectedRetailer, searchQuery, onlyWithAttachments, selectedTenderId]);

    const paginatedMessages = useMemo(() =>
        filteredMessages.slice((page - 1) * pageSize, page * pageSize),
        [filteredMessages, page, pageSize]
    );

    // Extract unique retailers for dropdown
    const retailers = Array.from(new Set(messages.map(m => (m as any).thread?.retailer?.name).filter(Boolean))).sort();

    return (
        <div className="h-full flex flex-col space-y-4">
            {/* Filters Bar */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row gap-4 items-end md:items-center">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4 w-full">
                            {/* Tender Filter */}
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-500">Tender</label>
                                <select
                                    className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                    value={selectedTenderId}
                                    onChange={(e) => setSelectedTenderId(e.target.value)}
                                >
                                    <option value="all">All Tenders</option>
                                    {tenders.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.opportunity_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Retailer Filter */}
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-500">Retailer</label>
                                <select
                                    className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                    value={selectedRetailer}
                                    onChange={(e) => setSelectedRetailer(e.target.value)}
                                >
                                    <option value="all">All Retailers</option>
                                    {retailers.map((r: any) => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Search */}
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-500">Search</label>
                                <div className="relative">
                                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Subject, email..."
                                        className="pl-8 h-9"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Checkbox */}
                            <div className="flex items-center space-x-2 h-9 mt-6">
                                <Checkbox
                                    id="attachments"
                                    checked={onlyWithAttachments}
                                    onCheckedChange={(checked) => setOnlyWithAttachments(checked as boolean)}
                                />
                                <label
                                    htmlFor="attachments"
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                    Has Attachments
                                </label>
                            </div>
                        </div>

                        <Button variant="outline" size="sm" onClick={fetchMessages} disabled={loading}>
                            <Filter className="w-4 h-4 mr-2" />
                            Apply
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Messages List */}
            <div className="flex-1 overflow-auto">
                {loading ? (
                    <div className="flex justify-center p-8">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                ) : filteredMessages.length === 0 ? (
                    <div className="text-center p-12 bg-gray-50 rounded-lg border border-dashed">
                        <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <h3 className="text-lg font-medium text-gray-900">No responses found</h3>
                        <p className="text-gray-500">Try adjusting your filters</p>
                    </div>
                ) : (
                    <div className="border rounded-lg overflow-hidden bg-white">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                                    <th className="py-2 pl-4 pr-3 text-left font-medium w-44">From</th>
                                    <th className="py-2 px-2 text-left font-medium">Subject</th>
                                    <th className="py-2 px-2 text-left font-medium w-32">Attachments</th>
                                    <th className="py-2 pl-2 pr-4 text-right font-medium w-36">Received</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedMessages.map((msg: any) => (
                                    <React.Fragment key={msg.id}>
                                        <tr className="border-b hover:bg-blue-50/30 transition-colors group">
                                            {/* From */}
                                            <td className="py-3 pl-4 pr-3 align-top">
                                                <div className="font-medium text-gray-900 truncate max-w-[10rem]">
                                                    {msg.thread?.retailer?.name || msg.sender_email}
                                                </div>
                                                <div className="text-xs text-gray-400 truncate">{msg.sender_email}</div>
                                            </td>

                                            {/* Subject + snippet + tender */}
                                            <td className="py-3 px-2 align-top">
                                                <div className="flex items-baseline gap-2">
                                                    <span className="font-medium text-gray-800 truncate max-w-[240px]">
                                                        {msg.subject}
                                                    </span>
                                                    {msg.thread?.tender?.opportunity_name && (
                                                        <span className="text-xs text-blue-500 shrink-0">
                                                            · {msg.thread.tender.opportunity_name}
                                                        </span>
                                                    )}
                                                </div>
                                                {msg.snippet && (
                                                    <p className="text-xs text-gray-400 truncate mt-0.5 max-w-sm">{msg.snippet}</p>
                                                )}
                                            </td>

                                            {/* Attachments inline */}
                                            <td className="py-3 px-2 align-top">
                                                {msg.attachments?.length > 0 ? (
                                                    <div className="space-y-1">
                                                        {msg.attachments.map((att: any) => (
                                                            <div key={att.id} className="flex items-center gap-1.5">
                                                                <Paperclip className="w-3 h-3 text-gray-400 shrink-0" />
                                                                <span className="text-xs text-gray-600 truncate max-w-[80px]" title={att.filename}>
                                                                    {att.filename}
                                                                </span>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-5 w-5 text-gray-400 hover:text-blue-600"
                                                                    onClick={() => handleDownload(att.id, att.filename, '')}
                                                                >
                                                                    <Download className="w-3 h-3" />
                                                                </Button>
                                                               
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-300">—</span>
                                                )}
                                            </td>

                                            {/* Date */}
                                            <td className="py-3 pl-2 pr-4 align-top text-right text-xs text-gray-400 whitespace-nowrap">
                                                {new Date(msg.received_at || msg.created_at).toLocaleString()}
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {filteredMessages.length > 0 && (
                <PaginationBar
                    page={page}
                    pageSize={pageSize}
                    total={filteredMessages.length}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                />
            )}
        </div>
    );
};
